import type { ComponentChildren, JSX } from 'preact'
import { keyframes, styled } from '../setup'
import { borders, colors, components, spacing, typography } from '../theme'

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

// Size tokens for buttons (dyslexia-friendly: 16px minimum)
const sizeStyles = {
  small: {
    fontSize: typography.fontSizeSm,
    padding: `${spacing.sm} ${spacing.lg}`,
  },
  medium: {
    fontSize: typography.fontSizeBase,
    padding: `${spacing.md} ${spacing['2xl']}`,
  },
  large: {
    fontSize: typography.fontSizeLg,
    padding: `${spacing.lg} ${spacing['3xl']}`,
  },
}

// Color variants (light theme compatible)
// Hover = lighter (not more saturated) for dyslexia accessibility
// Active = darker for clear press feedback
const variantStyles = {
  primary: {
    background: colors.accentPrimary,
    color: colors.accentText,
    border: colors.accentPrimary,
    hoverBg: colors.accentHover, // Lighter
    activeBg: colors.accentActive, // Darker
  },
  secondary: {
    background: colors.bgSurfaceAlt,
    color: colors.textSecondary,
    border: colors.border,
    hoverBg: '#ffffff', // Lighter
    activeBg: '#efefef', // Darker
  },
  danger: {
    background: colors.error,
    color: '#ffffff',
    border: colors.error,
    hoverBg: '#e53e3e', // Lighter red
    activeBg: '#b91c1c', // Darker red
  },
}

const StyledButton = styled('button')<{
  variant: 'primary' | 'secondary' | 'danger'
  size: 'small' | 'medium' | 'large'
  fullWidth?: boolean
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm};
  padding: ${(props) => sizeStyles[props.size].padding};
  font-size: ${(props) => sizeStyles[props.size].fontSize};
  font-weight: ${typography.fontWeightSemibold};
  border-radius: ${borders.radius};
  cursor: pointer;
  transition: ${components.button.transition};
  border: ${borders.widthMedium} solid ${(props) => variantStyles[props.variant].border};
  width: ${(props) => (props.fullWidth ? '100%' : 'auto')};
  background: ${(props) => variantStyles[props.variant].background};
  color: ${(props) => variantStyles[props.variant].color};

  &:hover:not(:disabled) {
    background: ${(props) => variantStyles[props.variant].hoverBg};
  }

  &:active:not(:disabled) {
    background: ${(props) => variantStyles[props.variant].activeBg};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const Spinner = styled('span')`
  width: 18px;
  height: 18px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`

interface ButtonProps {
  children: ComponentChildren
  variant?: 'primary' | 'secondary' | 'danger'
  size?: 'small' | 'medium' | 'large'
  type?: 'button' | 'submit'
  disabled?: boolean
  loading?: boolean
  fullWidth?: boolean
  onClick?: () => void
  style?: JSX.CSSProperties
}

export function Button({
  children,
  variant = 'secondary',
  size = 'medium',
  type = 'button',
  disabled,
  loading,
  fullWidth,
  onClick,
  style,
}: ButtonProps) {
  return (
    <StyledButton
      type={type}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      onClick={onClick}
      style={style}
    >
      {loading && <Spinner />}
      {children}
    </StyledButton>
  )
}
